import { useCallback, useEffect, useRef, useState } from 'react';
import { monitoringMessageHelper } from 'instrumentation/helpers';
import { SSE } from 'sse.js';
import { MessageType } from 'ts-components/Chat';
import { QuickResponseData } from 'ts-components/QuickResponses';
import AIChatService from '__services/AIChatService';
import { useSessionStorage } from 'hooks/useSessionStorage';
import {
  ConnectionState,
  HistoryMessage,
  UseChatStreamOptions,
  UseChatStreamReturn,
  AIChatStreamError,
  AcceptedEventData,
  StatusEventData,
  TokenEventData,
  ErrorEventData,
  HistoryEventData,
  PreviewItem,
  SpaceName,
} from './types';
import {
  RECONNECT_CONFIG,
  SEND_RETRY_CONFIG,
  SSE_EVENTS,
  CONNECTION_STATES,
  TURN_STORAGE_KEY_PREFIX,
  SPACE_NAMES,
  DEFAULT_PLACEHOLDER,
} from './constants';
import {
  parseButtonSpace,
  rehydrateMessages,
  normalizeHistoryMessage,
} from './rehydrateMessages';

const createUserMessage = (text: string, turnCount: number): MessageType => ({
  id: `user-${turnCount}-${Date.now()}`,
  isUserMessage: true,
  isResponseLoading: false,
  messageContent: text,
  role: 'user',
});

const createLoadingMessage = (turnCount: number): MessageType => ({
  id: `loading-${turnCount}-${Date.now()}`,
  isUserMessage: false,
  isResponseLoading: true,
  messageContent: '',
  role: 'assistant',
  bubble: false,
  showAvatar: false,
  floatAbove: true,
});

export const useChatStream = (
  options: UseChatStreamOptions
): UseChatStreamReturn => {
  const { sessionId, onError, onComplete } = options;
  const { getItem, setItem, removeItem } = useSessionStorage();

  const [messages, setMessages] = useState<MessageType[]>([]);
  const [quickResponseData, setQuickResponseData] = useState<QuickResponseData>(
    {
      options: [],
      selectType: 'action',
    }
  );
  const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER);
  const [turnCount, setTurnCount] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    CONNECTION_STATES.DISCONNECTED
  );
  const [alert, setAlert] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);

  const sseRef = useRef<SSE | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const sessionIdRef = useRef(sessionId);
  const turnCountRef = useRef(turnCount);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);
  const streamHistoryRef = useRef<MessageType[]>([]);
  const lastStatusSummaryRef = useRef<string>('');
  const lastAssistantContentRef = useRef<string>('');
  const attemptReconnectRef = useRef<() => void>(() => {});
  const pendingSendRef = useRef<{
    text: string;
    sessionId: string;
    lastTurnCount: number;
    attempts: number;
  } | null>(null);
  const sendRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retrySendRef = useRef<() => void>(() => {});
  const noEventTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  sessionIdRef.current = sessionId;
  turnCountRef.current = turnCount;
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  const clearSendRetryTimer = useCallback(() => {
    if (sendRetryTimerRef.current) {
      clearTimeout(sendRetryTimerRef.current);
      sendRetryTimerRef.current = null;
    }
  }, []);

  const clearNoEventTimer = useCallback(() => {
    if (noEventTimerRef.current) {
      clearTimeout(noEventTimerRef.current);
      noEventTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    clearSendRetryTimer();
    clearNoEventTimer();
    pendingSendRef.current = null;

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (sseRef.current) {
      try {
        sseRef.current.close();
      } catch {
        // Ignore close errors
      }
      sseRef.current = null;
    }
  }, [clearSendRetryTimer, clearNoEventTimer]);

  const abort = useCallback(() => {
    cleanup();
    setIsStreaming(false);
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    setAlert('');
  }, [cleanup]);

  const setWelcomeMessage = useCallback((content: string) => {
    const welcomeMsg: MessageType = {
      id: `welcome-${Date.now()}`,
      isUserMessage: false,
      isResponseLoading: false,
      messageContent: content,
      role: 'assistant',
      showFeedbackIcons: false,
    };
    streamHistoryRef.current = [welcomeMsg];
    setMessages([welcomeMsg]);
  }, []);

  const rehydrate = useCallback((historyMessages: HistoryMessage[]) => {
    const result = rehydrateMessages(historyMessages);

    setMessages(result.messages);
    setTurnCount(result.turnCount);
    setQuickResponseData(result.quickResponseData);
    setPlaceholder(result.placeholder);
    setPreviewData(result.previewData);
  }, []);

  const initializeSSE = useCallback(
    (sse: SSE, initialTurn: number) => {
      setIsStreaming(true);
      setConnectionState(CONNECTION_STATES.CONNECTED);
      abortControllerRef.current = new AbortController();

      let currentTurnCount = initialTurn;
      let isNewFrame = false;
      let assistantContent = lastAssistantContentRef.current;
      let statusSummaryContent = lastStatusSummaryRef.current;
      let statusDetailsContent = '';
      let userContent = '';
      let buttonContent = '';
      let placeholderContent = '';
      let previewContent: string | PreviewItem[] = '';
      let completedFrameMessages: MessageType[] = [];
      let frameIndex = 0;

      const accumulate = (space: SpaceName, content: string, mode?: string) => {
        switch (space) {
          case SPACE_NAMES.ASSISTANT_MESSAGE:
            assistantContent += content;
            lastAssistantContentRef.current = assistantContent;
            break;
          case SPACE_NAMES.STATUS_SUMMARY:
            statusSummaryContent = content;
            lastStatusSummaryRef.current = content;
            break;
          case SPACE_NAMES.STATUS_DETAILS:
            statusDetailsContent =
              mode === 'overwrite' ? content : statusDetailsContent + content;
            break;
          case SPACE_NAMES.USER_MESSAGE:
            userContent = content;
            break;
          case SPACE_NAMES.PLACEHOLDER:
            placeholderContent = content;
            break;
          case SPACE_NAMES.BUTTONS:
            buttonContent = content;
            break;
          case SPACE_NAMES.PREVIEWS:
            previewContent = content;
            break;
        }
      };

      const buildMessages = (isStreaming: boolean): MessageType[] => {
        const msgs: MessageType[] = [...completedFrameMessages];
        if (userContent)
          msgs.push({
            id: `user-${currentTurnCount}`,
            isUserMessage: true,
            isResponseLoading: false,
            messageContent: userContent,
            role: 'user',
          });
        // Keep the status row mounted while details exist, even when the
        // summary is overwritten to empty mid-turn — removing and re-adding
        // the row shifts the layout and jumps the scroll position
        if (statusSummaryContent || statusDetailsContent)
          msgs.push({
            id: `status-summary-${currentTurnCount}`,
            isUserMessage: false,
            isResponseLoading: false,
            isResponseRefining: isStreaming,
            messageContent: statusSummaryContent,
            role: 'assistant',
            streaming: isStreaming,
            bubble: false,
            showAvatar: false,
            floatAbove: false,
            statusDetails: statusDetailsContent || undefined,
          });
        if (assistantContent)
          msgs.push({
            id: `assistant-${currentTurnCount}`,
            isUserMessage: false,
            isResponseLoading: false,
            messageContent: assistantContent,
            streaming: isStreaming,
            isDoneStreaming: !isStreaming,
            showFeedbackIcons: !isStreaming,
            role: 'assistant',
          });
        const previews = getPreviewData();
        if (previews.length > 0) {
          msgs.push({
            id: `previews-${currentTurnCount}`,
            isUserMessage: false,
            isResponseLoading: false,
            messageContent: '',
            role: 'assistant',
            showFeedbackIcons: false,
            previewData: previews,
          });
        }
        return msgs;
      };

      const getQuickResponseData = (): QuickResponseData =>
        buttonContent
          ? parseButtonSpace(buttonContent)
          : { options: [], selectType: 'action' };

      const getPlaceholder = () => placeholderContent || DEFAULT_PLACEHOLDER;

      const getPreviewData = (): PreviewItem[] => {
        if (!previewContent) return [];
        try {
          const parsed =
            typeof previewContent === 'string'
              ? JSON.parse(previewContent)
              : previewContent;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      };

      const snapshotAndResetAssistantSpaces = () => {
        if (assistantContent) {
          completedFrameMessages.push({
            id: `assistant-${currentTurnCount}-f${frameIndex}`,
            isUserMessage: false,
            isResponseLoading: false,
            messageContent: assistantContent,
            streaming: false,
            isDoneStreaming: true,
            showFeedbackIcons: true,
            role: 'assistant',
          });
          frameIndex++;
        }
        assistantContent = '';
        lastAssistantContentRef.current = '';
        statusSummaryContent = '';
        lastStatusSummaryRef.current = '';
        statusDetailsContent = '';
        buttonContent = '';
        placeholderContent = '';
        previewContent = '';
      };

      const resetAll = () => {
        assistantContent = '';
        lastAssistantContentRef.current = '';
        statusSummaryContent = '';
        statusDetailsContent = '';
        userContent = '';
        buttonContent = '';
        placeholderContent = '';
        previewContent = '';
        completedFrameMessages = [];
        frameIndex = 0;
      };

      const flush = (isComplete: boolean) => {
        if (abortControllerRef.current?.signal.aborted) return;

        const currentMessages = buildMessages(!isComplete);
        setMessages([...streamHistoryRef.current, ...currentMessages]);

        setTurnCount(currentTurnCount);
        setQuickResponseData(getQuickResponseData());
        setPlaceholder(getPlaceholder());
        setPreviewData(getPreviewData());

        if (isComplete) {
          onCompleteRef.current?.(currentTurnCount);
          setMessages((prev) =>
            prev.map((m) => ({
              ...m,
              streaming: false,
              isDoneStreaming: true,
              isResponseLoading: false,
              isResponseRefining: false,
            }))
          );
        }
      };

      sse.addEventListener(SSE_EVENTS.ACCEPTED, (event: any) => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        try {
          const data = JSON.parse(event.data) as AcceptedEventData;
          currentTurnCount = data.turn_count;
          reconnectAttemptsRef.current = 0;
          // Server acknowledged the send — no retry needed; further drops
          // are recovered through the resume-based reconnect
          pendingSendRef.current = null;
          if (sendRetryTimerRef.current) {
            clearTimeout(sendRetryTimerRef.current);
            sendRetryTimerRef.current = null;
          }
        } catch (error) {
          monitoringMessageHelper('ChatBotV2 SSE parse error', {
            extra: { error },
          });
        }
      });

      sse.addEventListener(SSE_EVENTS.HISTORY, (event: any) => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        try {
          const data = JSON.parse(event.data) as HistoryEventData;
          const { messages: historyMsgs } = data;

          if (!historyMsgs.length) return;

          let maxTurnCount = 0;
          for (const msg of historyMsgs) {
            if (msg.turn_count > maxTurnCount) maxTurnCount = msg.turn_count;
          }

          const currentTurnMsgsFromHistory = historyMsgs.filter(
            (m) => m.turn_count === maxTurnCount
          );
          const isCurrentTurnInHistory =
            maxTurnCount === currentTurnCount &&
            !currentTurnMsgsFromHistory.some((m) => m.done === true);

          const pastMsgs = isCurrentTurnInHistory
            ? historyMsgs.filter((m) => m.turn_count < maxTurnCount)
            : historyMsgs;

          let addedPastMessages = false;
          if (pastMsgs.length > 0) {
            const { messages: pastMessages } = rehydrateMessages(pastMsgs);

            const normalizeId = (id: string) =>
              id.startsWith('user-') ? id.split('-').slice(0, 2).join('-') : id;

            const existingNormIds = new Set(
              streamHistoryRef.current.map((m) => normalizeId(m.id))
            );

            const newMessages = pastMessages.filter(
              (m) => !existingNormIds.has(normalizeId(m.id))
            );

            if (newMessages.length > 0) {
              addedPastMessages = true;
              const lastExisting = streamHistoryRef.current.at(-1);
              const currentUserMsg = lastExisting?.isUserMessage
                ? lastExisting
                : null;
              const preceding = currentUserMsg
                ? streamHistoryRef.current.slice(0, -1)
                : streamHistoryRef.current;

              streamHistoryRef.current = [
                ...preceding,
                ...newMessages,
                ...(currentUserMsg ? [currentUserMsg] : []),
              ];
            }
          }

          if (isCurrentTurnInHistory) {
            for (const rawMsg of currentTurnMsgsFromHistory) {
              const msg = normalizeHistoryMessage(rawMsg);
              if (!msg.space || msg.content === undefined) continue;
              if (msg.space === 'user_message') continue;
              if (msg.space === 'assistant_message') {
                assistantContent = msg.content;
                lastAssistantContentRef.current = msg.content;
              } else if (msg.space === 'status_summary')
                statusSummaryContent = msg.content;
              else if (msg.space === 'status_details')
                statusDetailsContent =
                  msg.mode === 'overwrite'
                    ? msg.content
                    : statusDetailsContent + msg.content;
              else if (msg.space === 'buttons' || msg.space === 'button')
                buttonContent = msg.content;
              else if (msg.space === 'placeholder')
                placeholderContent = msg.content;
              else if (msg.space === 'previews') previewContent = msg.content;
            }
          }

          const hasCurrentTurnContent =
            !!assistantContent || !!statusSummaryContent;
          if (
            addedPastMessages ||
            (isCurrentTurnInHistory && hasCurrentTurnContent)
          ) {
            flush(false);
          }
        } catch (error) {
          monitoringMessageHelper('ChatBotV2 SSE parse error', {
            extra: { error },
          });
        }
      });

      sse.addEventListener(SSE_EVENTS.STATUS, (event: any) => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        try {
          const data = JSON.parse(event.data) as StatusEventData;
          currentTurnCount = data.turn_count;

          if (data.error) {
            setConnectionState(CONNECTION_STATES.ERROR);
            onErrorRef.current?.(new Error(data.error));
            setMessages((prev) =>
              prev.filter((m) => !m.isResponseLoading && !m.isResponseRefining)
            );
            setIsStreaming(false);
            return;
          }

          if (data.alert && typeof data.alert === 'string') {
            setAlert(data.alert);
            setIsStreaming(false);
            flush(true);
            resetAll();
            isNewFrame = false;
            setIsStreaming(false);
            if (sessionIdRef.current) {
              removeItem(`${TURN_STORAGE_KEY_PREFIX}${sessionIdRef.current}`);
            }
            return;
          }

          if (data.new_frame && !isNewFrame) {
            isNewFrame = true;
            snapshotAndResetAssistantSpaces();
          }

          if (data.done) {
            flush(true);
            resetAll();
            isNewFrame = false;
            setIsStreaming(false);
            if (sessionIdRef.current) {
              removeItem(`${TURN_STORAGE_KEY_PREFIX}${sessionIdRef.current}`);
            }
            return;
          }

          if (data.space && data.content !== undefined) {
            accumulate(data.space, data.content, data.mode);
            flush(false);
          }

          if (sessionIdRef.current) {
            setItem(
              `${TURN_STORAGE_KEY_PREFIX}${sessionIdRef.current}`,
              String(data.turn_count)
            );
          }
        } catch (error) {
          monitoringMessageHelper('ChatBotV2 SSE parse error', {
            extra: { error },
          });
        }
      });

      sse.addEventListener(SSE_EVENTS.TOKEN, (event: any) => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        try {
          const data = JSON.parse(event.data) as TokenEventData;
          if (data.space && data.token) {
            accumulate(data.space, data.token);
            flush(false);
          }
        } catch (error) {
          monitoringMessageHelper('ChatBotV2 SSE parse error', {
            extra: { error },
          });
        }
      });

      sse.addEventListener(SSE_EVENTS.TIMEOUT, () => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        // Server-initiated reconnect (graceful connection rotation) — never
        // consume the failure budget for these
        reconnectAttemptsRef.current = 0;
        abortControllerRef.current?.abort();
        setConnectionState(CONNECTION_STATES.RECONNECTING);
        // An unacknowledged send is not recoverable through the resume
        // stream, so retry the send itself
        if (pendingSendRef.current) {
          retrySendRef.current();
          return;
        }
        attemptReconnectRef.current();
      });

      sse.addEventListener(SSE_EVENTS.ERROR, (event: any) => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        try {
          const data = JSON.parse(event.data) as ErrorEventData;
          setConnectionState(CONNECTION_STATES.ERROR);
          onErrorRef.current?.(new AIChatStreamError(data));
          setMessages((prev) =>
            prev.filter((m) => !m.isResponseLoading && !m.isResponseRefining)
          );
          setIsStreaming(false);
        } catch (error) {
          monitoringMessageHelper('ChatBotV2 SSE parse error', {
            extra: { error },
          });
        }
      });

      sse.onerror = () => {
        if (abortControllerRef.current?.signal.aborted) return;
        clearNoEventTimer();
        abortControllerRef.current?.abort();
        setConnectionState(CONNECTION_STATES.RECONNECTING);
        if (pendingSendRef.current) {
          retrySendRef.current();
          return;
        }
        attemptReconnectRef.current();
      };

      // Reconnect again if the stream opens but no events ever arrive
      if (!pendingSendRef.current) {
        clearNoEventTimer();
        noEventTimerRef.current = setTimeout(() => {
          if (abortControllerRef.current?.signal.aborted) return;
          abortControllerRef.current?.abort();
          setConnectionState(CONNECTION_STATES.RECONNECTING);
          attemptReconnectRef.current();
        }, RECONNECT_CONFIG.NO_EVENT_TIMEOUT_MS);
      }
    },
    [removeItem, setItem, clearNoEventTimer]
  );

  const attemptReconnect = useCallback(() => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    clearNoEventTimer();

    const attempts = reconnectAttemptsRef.current;
    if (attempts >= RECONNECT_CONFIG.MAX_ATTEMPTS) {
      setConnectionState(CONNECTION_STATES.ERROR);
      onErrorRef.current?.(new Error('Max reconnection attempts reached'));
      // Release the streaming state so the chat is usable again
      setMessages((prev) =>
        prev.filter((m) => !m.isResponseLoading && !m.isResponseRefining)
      );
      setIsStreaming(false);
      return;
    }

    const delay = Math.min(
      RECONNECT_CONFIG.BASE_DELAY_MS * Math.pow(2, attempts),
      RECONNECT_CONFIG.MAX_DELAY_MS
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current = attempts + 1;
      setConnectionState(CONNECTION_STATES.RECONNECTING);

      if (sseRef.current) {
        try {
          sseRef.current.close();
        } catch (error) {
          monitoringMessageHelper(
            'ChatBotV2 SSE close error during reconnect',
            {
              extra: { error },
            }
          );
        }
        sseRef.current = null;
      }
      abortControllerRef.current = null;
      const afterTurn = turnCountRef.current - 1;
      const endpoint = AIChatService.getMessagesStreamingEndpoint(
        currentSessionId,
        true,
        afterTurn
      );

      const sse = new SSE(endpoint, {
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        withCredentials: true,
      });

      sseRef.current = sse;
      initializeSSE(sse, turnCountRef.current);
    }, delay);
  }, [initializeSSE, clearNoEventTimer]);

  attemptReconnectRef.current = attemptReconnect;

  // Opens the send POST stream for the pending send and starts a timer
  // that retries the send if the server never acknowledges it
  const startSendStream = useCallback(() => {
    const pending = pendingSendRef.current;
    if (!pending) return;

    const endpoint = AIChatService.getSendMessageStreamingEndpoint(
      pending.sessionId
    );
    const sse = new SSE(endpoint, {
      payload: JSON.stringify({
        user_message: pending.text,
        last_turn_count: pending.lastTurnCount,
      }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      withCredentials: true,
    });

    sseRef.current = sse;
    initializeSSE(sse, pending.lastTurnCount + 1);

    clearSendRetryTimer();
    sendRetryTimerRef.current = setTimeout(() => {
      if (!pendingSendRef.current) return;
      retrySendRef.current();
    }, SEND_RETRY_CONFIG.NO_RESPONSE_TIMEOUT_MS);
  }, [initializeSSE, clearSendRetryTimer]);

  const retrySend = useCallback(() => {
    const pending = pendingSendRef.current;
    if (!pending) return;

    clearSendRetryTimer();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (sseRef.current) {
      try {
        sseRef.current.close();
      } catch {
        // Ignore close errors
      }
      sseRef.current = null;
    }

    if (pending.attempts >= SEND_RETRY_CONFIG.MAX_ATTEMPTS) {
      pendingSendRef.current = null;
      setConnectionState(CONNECTION_STATES.ERROR);
      onErrorRef.current?.(
        new Error('Failed to send message, please try again.')
      );
      setMessages((prev) =>
        prev.filter((m) => !m.isResponseLoading && !m.isResponseRefining)
      );
      setIsStreaming(false);
      return;
    }

    pending.attempts += 1;
    setConnectionState(CONNECTION_STATES.RECONNECTING);
    sendRetryTimerRef.current = setTimeout(
      () => startSendStream(),
      SEND_RETRY_CONFIG.RETRY_DELAY_MS * pending.attempts
    );
  }, [clearSendRetryTimer, startSendStream]);

  retrySendRef.current = retrySend;

  const sendMessage = useCallback(
    (text: string, sessionIdOverride?: string) => {
      const effectiveSessionId = sessionIdOverride || sessionId;
      if (!effectiveSessionId) {
        onErrorRef.current?.(new Error('No session ID provided'));
        return;
      }

      cleanup();
      setAlert('');
      reconnectAttemptsRef.current = 0;
      lastStatusSummaryRef.current = '';
      lastAssistantContentRef.current = '';
      removeItem(`${TURN_STORAGE_KEY_PREFIX}${effectiveSessionId}`);

      const nextTurnCount = turnCount + 1;
      const userMessage = createUserMessage(text, nextTurnCount);
      const loadingMessage = createLoadingMessage(nextTurnCount);

      setMessages((prev) => {
        streamHistoryRef.current = [...prev, userMessage];
        return [...prev, userMessage, loadingMessage];
      });
      setConnectionState(CONNECTION_STATES.CONNECTING);

      pendingSendRef.current = {
        text,
        sessionId: effectiveSessionId,
        lastTurnCount: turnCount,
        attempts: 0,
      };
      startSendStream();
    },
    [sessionId, turnCount, cleanup, removeItem, startSendStream]
  );

  const resumeStream = useCallback(
    (sessionIdOverride?: string) => {
      const currentSessionId = sessionIdOverride || sessionIdRef.current;
      if (!currentSessionId) return;

      sessionIdRef.current = currentSessionId;

      const stored = getItem(`${TURN_STORAGE_KEY_PREFIX}${currentSessionId}`);
      if (stored) turnCountRef.current = parseInt(stored, 10);

      cleanup();
      setConnectionState(CONNECTION_STATES.CONNECTING);
      const afterTurn = turnCountRef.current - 1;
      const endpoint = AIChatService.getMessagesStreamingEndpoint(
        currentSessionId,
        true,
        afterTurn
      );

      const sse = new SSE(endpoint, {
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        withCredentials: true,
      });

      sseRef.current = sse;
      initializeSSE(sse, turnCountRef.current);
    },
    [cleanup, initializeSSE, getItem]
  );

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setTurnCount(0);
      setQuickResponseData({ options: [], selectType: 'action' });
      setPlaceholder(DEFAULT_PLACEHOLDER);
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      setIsStreaming(false);
      setAlert('');
      setPreviewData([]);
    }
  }, [sessionId]);

  return {
    alert,
    messages,
    quickResponseData,
    placeholder,
    turnCount,
    isStreaming,
    connectionState,
    previewData,
    sendMessage,
    rehydrate,
    resumeStream,
    abort,
    setWelcomeMessage,
  };
};
